import React, { useEffect, useRef, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/dist/Draggable';
import { coffeeItems } from '../data/coffeeData';
import SearchModal from './SearchModal';
import './CoffeeSlider.css';

gsap.registerPlugin(Draggable);

const CoffeeSlider = ({ onSelectCoffee }) => {
  const sliderRef = useRef(null);
  const cardsRef = useRef([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const dragInstanceRef = useRef(null);
  const [extendedItems, setExtendedItems] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef(null);
  const animationFrameRef = useRef(null);
  const hasDraggedRef = useRef(false);
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    // Create extended array for infinite loop (duplicate items)
    const extended = [...coffeeItems, ...coffeeItems, ...coffeeItems];
    setExtendedItems(extended);
  }, []);

  useEffect(() => {
    if (extendedItems.length === 0) return;

    // Detect if mobile
    const isMobile = window.innerWidth <= 768;

    // Skip animations and draggable setup for mobile - use native scroll
    if (isMobile) {
      // Simple fade in for mobile cards
      gsap.fromTo(
        cardsRef.current.filter(card => card),
        { opacity: 0 },
        { opacity: 1, duration: 0.3, ease: 'power2.out' }
      );
      return; // Exit early, no draggable needed for mobile
    }

    // Desktop animations and setup
    gsap.fromTo(
      cardsRef.current.filter(card => card),
      {
        opacity: 0,
        y: 30
      },
      {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.05,
        ease: 'power2.out'
      }
    );

    // Set initial position to show middle set (desktop only)
    const cardWidth = 380;
    const cardGap = 40;
    const totalCardWidth = cardWidth + cardGap;
    const initialOffset = -(coffeeItems.length * totalCardWidth) - (cardWidth / 2);
    gsap.set(sliderRef.current, { x: initialOffset, y: 0 });

    // Set up draggable for desktop only
    if (sliderRef.current) {
      dragInstanceRef.current = Draggable.create(sliderRef.current, {
        type: 'x',
        inertia: true,
        dragResistance: 0.2,
        throwResistance: 2800,
        maxDuration: 0.8,
        minDuration: 0.25,
        overshootTolerance: 0,
        snap: {
          x: (value) => {
            const totalCardWidth = 420;
            const halfCard = 380 / 2;
            return Math.round((value + halfCard) / totalCardWidth) * totalCardWidth - halfCard;
          }
        },
        onPress: function() {
          hasDraggedRef.current = false;
          const isMobile = window.innerWidth <= 768;
          dragStartPos.current = { 
            x: this.x, 
            y: this.y,
            pos: isMobile ? this.y : this.x,
            time: Date.now() 
          };
        },
        onDrag: function() {
          // Only set isDragging if actual movement occurred (more than 5px)
          if (!hasDraggedRef.current && dragStartPos.current) {
            const isMobile = window.innerWidth <= 768;
            const currentPos = isMobile ? this.y : this.x;
            const dragDistance = Math.abs(currentPos - dragStartPos.current.pos);
            if (dragDistance > 5) {
              hasDraggedRef.current = true;
              setIsDragging(true);
            }
          }
          updateCardScales();
        },
        onDragEnd: function() {
          const isMobile = window.innerWidth <= 768;
          const pos = isMobile ? this.y : this.x;
          const totalCardSize = isMobile ? 520 : 420;
          
          let newIndex, snappedPos;
          if (isMobile) {
            const wrapperHeight = window.innerHeight - 180;
            const viewportCenter = wrapperHeight / 2;
            const cardCenter = 500 / 2;
            const centerOffset = viewportCenter - cardCenter;
            newIndex = Math.round((pos - centerOffset) / -totalCardSize);
            snappedPos = -newIndex * totalCardSize + centerOffset;
          } else {
            const halfCard = 190;
            newIndex = Math.round((pos + halfCard) / -totalCardSize);
            snappedPos = -newIndex * totalCardSize - halfCard;
          }
          
          // Ensure we snap to center
          gsap.to(sliderRef.current, {
            [isMobile ? 'y' : 'x']: snappedPos,
            duration: 0.4,
            ease: 'power2.inOut',
            onUpdate: updateCardScales,
            onComplete: () => {
              const isMobile = window.innerWidth <= 768;
              handleInfiniteWrap(newIndex, isMobile ? 520 : 420);
              if (dragInstanceRef.current && dragInstanceRef.current[0]) {
                dragInstanceRef.current[0].update();
              }
            }
          });
          
          if (hasDraggedRef.current) {
            setTimeout(() => {
              setIsDragging(false);
              hasDraggedRef.current = false;
            }, 50);
          }
        },
        onThrowUpdate: function() {
          updateCardScales();
        },
        onThrowComplete: function() {
          const isMobile = window.innerWidth <= 768;
          const pos = isMobile ? this.y : this.x;
          const totalCardSize = isMobile ? 520 : 420;
          
          let newIndex, snappedPos;
          if (isMobile) {
            const wrapperHeight = window.innerHeight - 180;
            const viewportCenter = wrapperHeight / 2;
            const cardCenter = 500 / 2;
            const centerOffset = viewportCenter - cardCenter;
            newIndex = Math.round((pos - centerOffset) / -totalCardSize);
            snappedPos = -newIndex * totalCardSize + centerOffset;
          } else {
            const halfCard = 190;
            newIndex = Math.round((pos + halfCard) / -totalCardSize);
            snappedPos = -newIndex * totalCardSize - halfCard;
          }
          
          // Ensure we snap to center after throw
          gsap.to(sliderRef.current, {
            [isMobile ? 'y' : 'x']: snappedPos,
            duration: 0.5,
            ease: 'power3.out',
            onUpdate: updateCardScales,
            onComplete: () => {
              const isMobile = window.innerWidth <= 768;
              handleInfiniteWrap(newIndex, isMobile ? 520 : 420);
              if (dragInstanceRef.current && dragInstanceRef.current[0]) {
                dragInstanceRef.current[0].update();
              }
            }
          });
          
          if (hasDraggedRef.current) {
            setTimeout(() => {
              setIsDragging(false);
              hasDraggedRef.current = false;
            }, 100);
          }
        }
      });
    }

    // Smooth continuous update for card scales
    const updateLoop = () => {
      updateCardScales();
      animationFrameRef.current = requestAnimationFrame(updateLoop);
    };
    updateLoop();

    return () => {
      if (dragInstanceRef.current && dragInstanceRef.current[0]) {
        dragInstanceRef.current[0].kill();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [extendedItems]);

  const handleInfiniteWrap = useCallback((newIndex, totalCardSize) => {
    // Enhanced infinite loop with seamless wrapping
    const isMobile = window.innerWidth <= 768;
    
    let offset;
    if (isMobile) {
      const wrapperHeight = window.innerHeight - 180;
      const viewportCenter = wrapperHeight / 2;
      const cardCenter = 500 / 2;
      const centerOffset = viewportCenter - cardCenter;
      
      if (newIndex >= extendedItems.length - coffeeItems.length / 2) {
        const resetIndex = coffeeItems.length;
        offset = -resetIndex * totalCardSize + centerOffset;
        gsap.set(sliderRef.current, { y: offset });
        if (dragInstanceRef.current && dragInstanceRef.current[0]) {
          dragInstanceRef.current[0].update();
        }
      } else if (newIndex < coffeeItems.length / 2) {
        const resetIndex = extendedItems.length - coffeeItems.length * 1.5;
        offset = -resetIndex * totalCardSize + centerOffset;
        gsap.set(sliderRef.current, { y: offset });
        if (dragInstanceRef.current && dragInstanceRef.current[0]) {
          dragInstanceRef.current[0].update();
        }
      }
    } else {
      const halfCard = 190;
      
      if (newIndex >= extendedItems.length - coffeeItems.length / 2) {
        const resetIndex = coffeeItems.length;
        offset = -resetIndex * totalCardSize - halfCard;
        gsap.set(sliderRef.current, { x: offset });
        if (dragInstanceRef.current && dragInstanceRef.current[0]) {
          dragInstanceRef.current[0].update();
        }
      } else if (newIndex < coffeeItems.length / 2) {
        const resetIndex = extendedItems.length - coffeeItems.length * 1.5;
        offset = -resetIndex * totalCardSize - halfCard;
        gsap.set(sliderRef.current, { x: offset });
        if (dragInstanceRef.current && dragInstanceRef.current[0]) {
          dragInstanceRef.current[0].update();
        }
      }
    }
    
    setCurrentIndex(newIndex % coffeeItems.length);
  }, [extendedItems, coffeeItems.length]);

  const updateCardScales = useCallback(() => {
    if (!sliderRef.current) return;
    
    const isMobile = window.innerWidth <= 768;
    let containerCenter;
    
    if (isMobile) {
      // Mobile: center is based on slider-wrapper height, not full viewport
      const wrapperHeight = window.innerHeight - 180; // Match CSS calc(100vh - 180px)
      const headerHeight = 180; // Approximate header + padding height
      containerCenter = headerHeight + (wrapperHeight / 2);
    } else {
      containerCenter = window.innerWidth / 2;
    }
    
    cardsRef.current.forEach((card, index) => {
      if (!card) return;
      
      const cardRect = card.getBoundingClientRect();
      const cardCenter = isMobile 
        ? cardRect.top + cardRect.height / 2 
        : cardRect.left + cardRect.width / 2;
      const distanceFromCenter = Math.abs(containerCenter - cardCenter);
      const maxDistance = isMobile ? 400 : 600;
      
      // Enhanced scale calculations with smooth curves
      const normalizedDistance = Math.min(distanceFromCenter / maxDistance, 1);
      // Easing function for smoother falloff
      const eased = 1 - Math.pow(normalizedDistance, 1.5);
      
      if (isMobile) {
        // Mobile: simpler scaling, no rotation, less blur
        const scale = 0.85 + (eased * 0.15); // 0.85 to 1
        const opacity = 0.5 + (eased * 0.5); // 0.5 to 1
        const blur = (1 - eased) * 1; // 0 to 1px blur
        const zIndex = Math.round(eased * 100);
        
        gsap.set(card, {
          scale: scale,
          opacity: opacity,
          filter: `blur(${blur}px)`,
          zIndex: zIndex,
          force3D: true
        });
        
        // Active state for mobile (tighter threshold)
        if (distanceFromCenter < 100) {
          if (!card.classList.contains('active-card')) {
            card.classList.add('active-card');
          }
        } else {
          card.classList.remove('active-card');
        }
      } else {
        // Desktop: full 3D effects
        const scale = 0.7 + (eased * 0.3); // 0.7 to 1
        const opacity = 0.4 + (eased * 0.6); // 0.4 to 1
        const blur = (1 - eased) * 2; // 0 to 2px blur
        const rotateY = (containerCenter - cardCenter) / 50; // Subtle 3D rotation
        const zIndex = Math.round(eased * 100);
        
        // Apply transforms with better performance
        gsap.set(card, {
          scale: scale,
          opacity: opacity,
          rotateY: rotateY,
          z: zIndex * 5,
          filter: `blur(${blur}px)`,
          force3D: true
        });
        
        // Enhanced active state with tighter threshold - only truly centered card
        if (distanceFromCenter < 50) {
          if (!card.classList.contains('active-card')) {
            card.classList.add('active-card');
          }
        } else {
          card.classList.remove('active-card');
        }
      }
    });
  }, []);

  useEffect(() => {
    // Animate title
    gsap.fromTo(
      '.slider-title',
      {
        opacity: 0,
        y: -20
      },
      {
        opacity: 1,
        y: 0,
        duration: 0.5,
        ease: 'power2.out'
      }
    );
  }, []);

  const handleCardClick = useCallback((coffee, isButtonClick = false) => {
    console.log('handleCardClick called', { coffee: coffee.name, isButtonClick, isDragging });
    
    const isMobile = window.innerWidth <= 768;
    
    // Mobile: simple direct order on click, no animations
    if (isMobile) {
      onSelectCoffee(coffee);
      return;
    }
    
    if (isDragging) {
      console.log('Blocked: isDragging is true');
      return; // Prevent clicks during drag
    }
    
    const containerCenter = window.innerWidth / 2;
    
    // Find the card instance that's closest to center (important for infinite scroll duplicates)
    const matchingCards = cardsRef.current.filter(el => el && el.dataset.id === String(coffee.id));
    let cardElement = null;
    let minDistance = Infinity;
    
    matchingCards.forEach(card => {
      const rect = card.getBoundingClientRect();
      const center = isMobile 
        ? rect.top + rect.height / 2 
        : rect.left + rect.width / 2;
      const dist = Math.abs(containerCenter - center);
      if (dist < minDistance) {
        minDistance = dist;
        cardElement = card;
      }
    });
    
    if (!cardElement) {
      console.log('Card element not found');
      return;
    }
    
    const cardRect = cardElement.getBoundingClientRect();
    const cardCenter = isMobile 
      ? cardRect.top + cardRect.height / 2 
      : cardRect.left + cardRect.width / 2;
    const distanceFromCenter = Math.abs(containerCenter - cardCenter);
    const hasActiveClass = cardElement.classList.contains('active-card');
    
    console.log('Card check:', { 
      distanceFromCenter, 
      hasActiveClass, 
      isButtonClick,
      containerCenter,
      cardCenter,
      matchingCardsCount: matchingCards.length
    });
    
    // If button click and card has active-card class, open modal directly
    if (isButtonClick && hasActiveClass) {
      console.log('Opening modal: button click on active card');
      // Ripple effect animation
      gsap.fromTo(cardElement,
        { boxShadow: '0 0 0 0 rgba(0, 0, 0, 0.2)' },
        {
          boxShadow: '0 0 0 15px rgba(0, 0, 0, 0)',
          duration: 0.4,
          ease: 'power2.out'
        }
      );
      
      // Scale pulse animation
      gsap.to(cardElement, {
        scale: 1.02,
        duration: 0.1,
        yoyo: true,
        repeat: 1,
        ease: 'power1.inOut'
      });
      
      onSelectCoffee(coffee);
      return;
    }
    
    // Only open modal if card is centered (for card clicks)
    if (distanceFromCenter < 100) {
      console.log('Opening modal: distance check passed');
      // Ripple effect animation
      gsap.fromTo(cardElement,
        { boxShadow: '0 0 0 0 rgba(0, 0, 0, 0.2)' },
        {
          boxShadow: '0 0 0 15px rgba(0, 0, 0, 0)',
          duration: 0.4,
          ease: 'power2.out'
        }
      );
      
      // Scale pulse animation
      gsap.to(cardElement, {
        scale: 1.02,
        duration: 0.1,
        yoyo: true,
        repeat: 1,
        ease: 'power1.inOut'
      });
      
      onSelectCoffee(coffee);
    } else {
      console.log('Scrolling to center: distance too far');
      // Scroll to center if not centered
      const isMobile = window.innerWidth <= 768;
      const cardSize = isMobile ? 540 : 420;
      const currentPos = gsap.getProperty(sliderRef.current, isMobile ? 'y' : 'x');
      const offset = containerCenter - cardCenter;
      const targetPos = currentPos + offset;
      
      gsap.to(sliderRef.current, {
        [isMobile ? 'y' : 'x']: targetPos,
        duration: 0.5,
        ease: 'power3.out',
        onUpdate: updateCardScales,
        onComplete: () => {
          if (dragInstanceRef.current && dragInstanceRef.current[0]) {
            dragInstanceRef.current[0].update();
          }
        }
      });
      
      // Subtle indication animation
      gsap.fromTo(cardElement,
        { x: 0 },
        {
          x: 3,
          duration: 0.1,
          repeat: 2,
          yoyo: true,
          ease: 'power1.inOut',
          onComplete: () => gsap.set(cardElement, { x: 0 })
        }
      );
    }
  }, [isDragging, onSelectCoffee, updateCardScales]);

  const navigateSlider = (direction) => {
    if (!sliderRef.current) return;
    
    const currentX = gsap.getProperty(sliderRef.current, 'x');
    const cardWidth = 420;
    let newIndex = Math.round(-currentX / cardWidth) + direction;
    
    // Infinite loop logic
    if (newIndex >= extendedItems.length - coffeeItems.length / 2) {
      newIndex = coffeeItems.length;
      gsap.set(sliderRef.current, { x: -newIndex * cardWidth });
    } else if (newIndex < coffeeItems.length / 2) {
      newIndex = extendedItems.length - coffeeItems.length - 1;
      gsap.set(sliderRef.current, { x: -newIndex * cardWidth });
    }
    
    gsap.to(sliderRef.current, {
      x: -newIndex * cardWidth,
      duration: 0.6,
      ease: 'power3.out',
      onUpdate: updateCardScales,
      onComplete: () => {
        setCurrentIndex(newIndex % coffeeItems.length);
      }
    });

    if (dragInstanceRef.current && dragInstanceRef.current[0]) {
      dragInstanceRef.current[0].update();
    }
  };

  return (
    <div className="coffee-slider-container">
      <div className="header">
        <h1 className="slider-title">
          <span className="title-main">AutoChef Barista</span>
          <span className="title-subtitle">Artisan Coffee • Made Fresh</span>
        </h1>
      </div>

      <div className="slider-wrapper">
        <div className="slider-track" ref={sliderRef}>
          {(window.innerWidth <= 768 ? coffeeItems : extendedItems).map((coffee, index) => (
            <div
              key={`${coffee.id}-${index}`}
              ref={(el) => (cardsRef.current[index] = el)}
              data-id={coffee.id}
              className="coffee-card"
              onClick={() => handleCardClick(coffee)}
            >
              <div className="card-image-container">
                <img 
                  src={coffee.imagePath} 
                  alt={coffee.name}
                  className="coffee-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="image-placeholder" style={{ display: 'none' }}>
                  <span className="placeholder-icon">☕</span>
                </div>
                <div className="image-overlay"></div>
              </div>
              <div className="card-content">
                <span className="coffee-type">{coffee.type}</span>
                <h2 className="coffee-name">{coffee.name}</h2>
                <p className="coffee-description">{coffee.description}</p>
                <div className="card-footer">
                  <div className="coffee-price">{coffee.price}</div>
                  <button 
                    className="quick-order-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCardClick(coffee, true);
                    }}
                  >
                    Order Now
                  </button>
                </div>
              </div>
              <div className="card-corners">
                <span className="corner corner-tl"></span>
                <span className="corner corner-tr"></span>
                <span className="corner corner-bl"></span>
                <span className="corner corner-br"></span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button 
        className="search-fab"
        onClick={() => setShowSearch(true)}
        aria-label="Search coffee"
      >
        <svg className="search-fab-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.35-4.35"></path>
        </svg>
      </button>

      {showSearch && (
        <SearchModal
          onClose={() => setShowSearch(false)}
          onSelectCoffee={onSelectCoffee}
        />
      )}
    </div>
  );
};

export default CoffeeSlider;
